import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Fuel, Heart, Send, Sparkles } from "lucide-react";
import { StripeTrustBadge } from "@/components/site/StripeTrustBadge";
import { openCheckoutWithPromise } from "@/lib/checkout";

const PRESETS = [
  { amount: 5, label: "Coffee" },
  { amount: 10, label: "Diner" },
  { amount: 25, label: "Tank fill" },
  { amount: 50, label: "Two tanks" },
  { amount: 100, label: "Day on the road" },
  { amount: 250, label: "Full week" },
];

export default function FuelPage() {
  const [amount, setAmount] = useState(25);
  const [custom, setCustom] = useState("");
  const [form, setForm] = useState({ name: "", email: "", message: "", anonymous: false });
  const [loading, setLoading] = useState(false);
  const [totals, setTotals] = useState({ total: 0, count: 0, goal: 50000, percent: 0 });
  const [wall, setWall] = useState([]);

  const load = () => {
    api.get("/fuel/totals").then((r) => setTotals(r.data || {}));
    api.get("/fuel/wall").then((r) => setWall(r.data || []));
  };
  useEffect(() => { load(); }, []);

  const finalAmount = custom ? parseFloat(custom) : amount;

  const donate = async () => {
    if (!finalAmount || finalAmount < 1) {
      toast("Pick an amount of at least $1.");
      return;
    }
    setLoading(true);
    try {
      await openCheckoutWithPromise(async () => {
        const { data } = await api.post("/fuel/checkout", {
          amount: Number(finalAmount.toFixed(2)),
          name: form.anonymous ? null : form.name || null,
          email: form.email.trim() || null,
          message: form.message || null,
          anonymous: form.anonymous,
          origin_url: window.location.origin,
        });
        return data.url;
      });
    } catch (e) {
      console.warn("fuel checkout failed:", e?.response?.status || e?.message);
      toast("Could not start checkout. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="fuel-page">
      {/* Hero */}
      <section className="relative grain splatter overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-8 pt-16 pb-12 grid md:grid-cols-12 gap-10 items-start relative z-10">
          <div className="md:col-span-7">
            <div className="overline flex items-center gap-2">
              <Fuel size={12} /> Fuel the journey
            </div>
            <h1 className="font-display text-6xl md:text-8xl uppercase mt-3 leading-[0.85] drip-shadow">
              Keep the Sentra
              <br />
              <span className="text-[#FF1493]">on the road.</span>
            </h1>
            <p className="text-white/75 mt-7 text-lg max-w-2xl">
              Gas, repairs, batteries, lights, coffee at 4am, the next 36 states. Every dollar goes
              into the build. Drop $5 or drop $500 — it all turns into miles.
            </p>

            {/* Progress bar */}
            <div className="mt-10 max-w-2xl" data-testid="fuel-progress">
              <div className="flex items-end justify-between mb-2">
                <div>
                  <div className="font-display text-4xl md:text-5xl text-[#FFC107]">
                    ${totals.total.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 font-mono-tactical">
                    raised by {totals.count} fans
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-xl text-white/70">
                    ${totals.goal.toLocaleString()}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 font-mono-tactical">
                    goal
                  </div>
                </div>
              </div>
              <div className="h-3 bg-white/5 border border-white/10 relative overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#FF1E1E] via-[#FF1493] to-[#FFC107] transition-all"
                  style={{ width: `${Math.max(2, totals.percent)}%` }}
                />
                <div className="absolute inset-0 splatter opacity-30 pointer-events-none" />
              </div>
              <div className="text-xs font-mono-tactical text-white/50 uppercase tracking-[0.2em] mt-2">
                {totals.percent}% to the finish line · Powered by Stripe
              </div>
            </div>
          </div>

          {/* Donation card */}
          <div className="md:col-span-5">
            <div className="border border-[#FF1493]/40 bg-[#141414] p-7 splatter relative overflow-hidden" data-testid="fuel-card">
              <div className="absolute -top-10 -right-10 w-72 h-72 bg-[#FF1493] opacity-10 blur-3xl rounded-full" />
              <div className="relative">
                <div className="overline">Pick your gas tank</div>
                <div className="grid grid-cols-3 gap-2 mt-4">
                  {PRESETS.map((p) => (
                    <button
                      key={p.amount}
                      onClick={() => {
                        setAmount(p.amount);
                        setCustom("");
                      }}
                      className={`p-3 border text-left transition ${
                        !custom && amount === p.amount
                          ? "border-[#FF1493] bg-[#FF1493]/10"
                          : "border-white/10 hover:border-white/30"
                      }`}
                      data-testid={`fuel-preset-${p.amount}`}
                    >
                      <div className="font-display text-2xl">${p.amount}</div>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-mono-tactical">
                        {p.label}
                      </div>
                    </button>
                  ))}
                </div>
                <div className="mt-3">
                  <label className="overline">Custom</label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="font-display text-2xl text-white/70">$</span>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      placeholder="Any amount"
                      className="tac-input"
                      value={custom}
                      onChange={(e) => setCustom(e.target.value)}
                      data-testid="fuel-custom"
                    />
                  </div>
                </div>

                <div className="divider-x my-5" />
                <input
                  placeholder="Your name (shown on the wall)"
                  className="tac-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  disabled={form.anonymous}
                  data-testid="fuel-name"
                />
                <input
                  type="email"
                  placeholder="Email for receipt (optional)"
                  className="tac-input mt-2"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="fuel-email"
                />
                <textarea
                  rows={2}
                  placeholder="Drop a one-liner (optional)"
                  className="tac-input mt-2"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="fuel-message"
                />
                <label className="flex items-center gap-2 mt-3 text-xs text-white/70 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.anonymous}
                    onChange={(e) => setForm({ ...form, anonymous: e.target.checked })}
                    data-testid="fuel-anonymous"
                  />
                  Show me as Anonymous
                </label>

                <button
                  onClick={donate}
                  disabled={loading}
                  className="btn-blaze w-full justify-center mt-5"
                  data-testid="fuel-donate"
                >
                  <Heart size={14} />
                  {loading ? "Loading..." : `Fuel $${finalAmount || 0}`}
                </button>
                <div className="mt-3 flex justify-center">
                  <StripeTrustBadge />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wall of fuel */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 py-16">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-7">
          <div>
            <div className="overline flex items-center gap-2">
              <Sparkles size={12} /> Fueled by
            </div>
            <h2 className="font-display text-4xl md:text-5xl uppercase mt-3">The wall</h2>
          </div>
          <div className="text-xs font-mono-tactical text-white/50 uppercase tracking-[0.25em]">
            Every dollar in pixels, forever
          </div>
        </div>

        {wall.length === 0 ? (
          <div className="border border-white/10 bg-[#141414] p-10 text-center text-white/60 text-sm">
            Be the first to fuel the journey.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {wall.map((d) => (
              <article key={d.id} className="tac-card p-5" data-testid={`wall-${d.id}`}>
                <div className="flex items-baseline justify-between">
                  <span className="font-display text-3xl text-[#FFC107]">
                    ${d.amount.toFixed(0)}
                  </span>
                  <span className="text-[10px] tracking-[0.2em] uppercase font-mono-tactical text-white/40">
                    {new Date(d.created_at).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                </div>
                <div className="font-display text-lg uppercase mt-3">
                  {d.anonymous ? "Anonymous" : d.display_name || "Anonymous"}
                </div>
                {d.message && (
                  <p className="text-white/70 text-sm mt-2 italic">"{d.message}"</p>
                )}
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
